import React, { useState, useEffect } from 'react'
import { ArrowLeft, Download, Calendar } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../config/api'
import toast from 'react-hot-toast'

const BulkDownload = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [availableYears, setAvailableYears] = useState([])

  // Verificar permisos
  const isAdmin = user?.role === 'administrador' || user?.is_admin
  const isSupervisor = user?.role === 'supervisor'

  useEffect(() => {
    if (!isAdmin && !isSupervisor) {
      toast.error('No tienes permisos para acceder a esta sección')
      navigate('/dashboard')
      return
    }

    // Generar años disponibles (últimos 10 años hasta el año actual)
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = 0; i < 10; i++) {
      years.push(currentYear - i)
    }
    setAvailableYears(years)
  }, [isAdmin, isSupervisor, navigate])

  const handleDownload = async () => {
    if (!year) {
      toast.error('Por favor selecciona un año')
      return
    }

    setIsDownloading(true)
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) {
        toast.error('No hay token de autenticación')
        setIsDownloading(false)
        return
      }

      // Construir URL con parámetros
      let url = `${API_BASE_URL}/api/documentos/bulk-download?year=${year}`
      if (month) {
        url += `&month=${month}`
      }

      // Realizar la descarga
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        let errorMessage = `Error ${response.status}`
        try {
          const errorData = await response.json()
          // Manejar diferentes formatos de respuesta de error
          if (typeof errorData === 'string') {
            errorMessage = errorData
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (Array.isArray(errorData.detail)) {
            // FastAPI puede devolver errores de validación como array
            errorMessage = errorData.detail.map(err => err.msg || err).join(', ')
          }
        } catch (e) {
          // Si no se puede parsear JSON, usar el texto de la respuesta
          const text = await response.text().catch(() => '')
          errorMessage = text || errorMessage
        }
        throw new Error(errorMessage)
      }

      // Obtener el blob
      const blob = await response.blob()

      // Crear nombre del archivo
      const filename = month 
        ? `documentos_${year}_${String(month).padStart(2, '0')}.zip`
        : `documentos_${year}.zip`

      // Crear enlace de descarga
      const url_blob = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url_blob
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url_blob)

      toast.success(`Descarga iniciada: ${filename}`)
    } catch (error) {
      console.error('Error en descarga masiva:', error)
      toast.error(error.message || 'Error al descargar documentos')
    } finally {
      setIsDownloading(false)
    }
  }

  if (!isAdmin && !isSupervisor) {
    return null
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background-color)'}}>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <button
              onClick={() => navigate('/config')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Volver</span>
            </button>
            <div className="header-title">
              <h1>Descarga Masiva de Documentos</h1>
              <p>Descarga múltiples documentos completados filtrados por año y mes</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Card principal */}
        <div className="card">
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Download className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Filtros de Descarga</h2>
                <p className="text-sm text-gray-600">Selecciona el año y opcionalmente el mes para descargar documentos</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Selector de Año */}
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-2" />
                Año <span className="text-red-500">*</span>
              </label>
              <select
                id="year"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="input-field w-full"
                required
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector de Mes (Opcional) */}
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-2" />
                Mes (Opcional)
              </label>
              <select
                id="month"
                value={month || ''}
                onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : null)}
                className="input-field w-full"
              >
                <option value="">Todos los meses</option>
                <option value="1">Enero</option>
                <option value="2">Febrero</option>
                <option value="3">Marzo</option>
                <option value="4">Abril</option>
                <option value="5">Mayo</option>
                <option value="6">Junio</option>
                <option value="7">Julio</option>
                <option value="8">Agosto</option>
                <option value="9">Septiembre</option>
                <option value="10">Octubre</option>
                <option value="11">Noviembre</option>
                <option value="12">Diciembre</option>
              </select>
            </div>

            {/* Botón de descarga */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleDownload}
                disabled={isDownloading || !year}
                className={`btn-primary w-full flex items-center justify-center space-x-2 ${
                  isDownloading || !year
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                {isDownloading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Descargando...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5" />
                    <span>Descargar Documentos</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Información adicional */}
          <div className="mt-8 bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Información</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Solo se descargan documentos con estado <strong>completado</strong></li>
              <li>• Si no seleccionas un mes, se descargarán todos los documentos del año</li>
              <li>• Los documentos se descargan en un archivo ZIP</li>
              <li>• El nombre del archivo incluye el año y mes (si se especifica)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkDownload

